/**
 * Favotrip Exclusive op favotrip.de: de publieke Duitse hub en hotelteaser
 * (brief A1). Alleen publieke regels, zonder serverafhankelijkheden, zodat de
 * middleware, de pagina's, de aanmeldproxy en de browser dezelfde regel lezen.
 *
 *   - Achter de schakelaar LUXE_DE_PUBLIEK_LIVE (luxe-de-publiek-flags.js):
 *     gebruik `luxeDePubliekAan(host)`, niet `isFavotripDeHost`.
 *   - `/exclusive` en `/exclusive/<id>` herschrijft de middleware op .de
 *     naar het interne pad `LUXE_DE_INTERN_PAD[/<id>]`; dat interne pad
 *     zelf is publiek een 404. Elk ander Exclusive-adres op .de (oude
 *     /luxe-adressen, een naam, een andere spelling) krijgt één 308 binnen
 *     .de (`luxeDeBestemming`), nooit naar favotrip.nl.
 *   - Data alleen uit `GET /luxe-public-de/deals[/<naam>]` (de naam alleen
 *     server-side, als sleutel bij de backend); nooit een prijs,
 *     korting of Nederlandse claim (exclusiveClaimToegestaan is op .de altijd
 *     false).
 */
import { isFavotripDeHost } from "./ing-de";
import { luxeDePubliekLive } from "./luxe-de-publiek-flags";
import { luxePublicIndexingLive } from "./luxe-public-flags";
import { EXCLUSIVE_PAD } from "./luxe-naam";

/** Het interne pad van de Duitse Exclusive-pagina's (de middleware herschrijft hierheen). */
export const LUXE_DE_INTERN_PAD = "/exclusive-de-voordeur";

/** De aanmeldroute van de backend (via de .de-klantproxy, contract brief A2). */
export const LUXE_DE_AANMELD_PAD = "/exclusive-toegang/aanmelden";

/**
 * Het anker van het Duitse aanmeldblok (bovenaan hub en teaser). De
 * inlogknop in de kop wijst op de Duitse Exclusive-pagina's hierheen, niet
 * naar de Nederlandse /account/login (fixronde 1 op fe#1937): inloggen en
 * lid worden zijn op .de dezelfde stap, een inloglink via de .de-proxy.
 */
export const LUXE_DE_AANMELD_ANKER = "exclusive-anmelden";

/**
 * De inloglink van de kop op een Duitse Exclusive-pagina: het aanmeldblok op
 * dit pad (`returnTo`, al gevalideerd door exclusiveAccountReturnTo). Op elk
 * ander domein, met de schakelaar uit of buiten /exclusive: null (dan blijft
 * de gewone /account/login).
 */
export function luxeDeInlogHref(domain, returnTo) {
  if (!luxeDePubliekAan(domain || "") || typeof returnTo !== "string") return null;
  if (returnTo !== EXCLUSIVE_PAD && !returnTo.startsWith(`${EXCLUSIVE_PAD}/`)) return null;
  return `${returnTo}#${LUXE_DE_AANMELD_ANKER}`;
}

/** Waar een mislukte of verlopen inloglink op .de landt als alleen deze schakelaar aan staat. */
export const LUXE_DE_INLOG_FOUT_PARAM = "anmeldung";
export const LUXE_DE_INLOG_FOUT_WAARDE = "abgelaufen";
export const LUXE_DE_INLOG_FOUT_PAD = `${EXCLUSIVE_PAD}?${LUXE_DE_INLOG_FOUT_PARAM}=${LUXE_DE_INLOG_FOUT_WAARDE}`;

/**
 * Gelden de Duitse Exclusive-pagina's voor deze host of dit domein? Alleen op
 * favotrip.de (www, kaal of staging) en alleen met de schakelaar aan.
 */
export function luxeDePubliekAan(hostOrDomain) {
  return luxeDePubliekLive() && isFavotripDeHost(hostOrDomain);
}

/** Als `luxeDePubliekAan`, voor de huidige browserhost (op de server: false). */
export function luxeDePubliekInBrowser() {
  if (typeof window === "undefined") return false;
  try {
    return luxeDePubliekAan(window.location.hostname);
  } catch {
    return false;
  }
}

/**
 * Fixronde 5 op fe#1937 (Codex op a43848b6): een slug uit de dealdata
 * ("kempinski-ab-99-euro-pro-nacht") draagt zelf een prijs en ging als
 * hub-link, aanmeldprop en canonical naar de anonieme browser. Op .de is het
 * adres van een hotel daarom zijn dealnummer (`/exclusive/<id>`); geen tekst
 * uit de dealdata komt nog in een adres. Alleen een positief geheel getal van
 * hooguit negen cijfers, zonder voorloopnul.
 */
const LUXE_DE_ID = /^[1-9]\d{0,8}$/;

/** Het dealnummer als adresdeel ("6101"), of null. Neemt een getal of een cijferreeks. */
export function luxeDeId(waarde) {
  const tekst = Number.isSafeInteger(waarde) ? String(waarde) : waarde;
  return typeof tekst === "string" && LUXE_DE_ID.test(tekst) ? tekst : null;
}

/**
 * Wat een canoniek Exclusive-pad (zoals resolveExclusiveRoute in de
 * middleware het bepaalt) op .de wordt:
 *   - `/exclusive` -> `{ intern: LUXE_DE_INTERN_PAD }`;
 *   - `/exclusive/<id>` -> `{ intern: LUXE_DE_INTERN_PAD/<id> }`;
 *   - al het andere (een naam, een tweede segment): `{ pad: "/exclusive" }`.
 *     Een naam is op .de geen adres (fixronde 5); de Nederlandse naamzoeker
 *     wordt op .de niet gevraagd.
 *
 * @param {string} canoniekPad
 * @returns {{ intern: string } | { pad: string }}
 */
export function luxeDeBestemming(canoniekPad) {
  if (canoniekPad === EXCLUSIVE_PAD) return { intern: LUXE_DE_INTERN_PAD };
  if (typeof canoniekPad === "string" && canoniekPad.startsWith(`${EXCLUSIVE_PAD}/`)) {
    const id = luxeDeId(canoniekPad.slice(EXCLUSIVE_PAD.length + 1));
    if (id) return { intern: [LUXE_DE_INTERN_PAD, id].join("/") };
  }
  return { pad: EXCLUSIVE_PAD };
}

/**
 * Mag deze Duitse pagina geïndexeerd worden? Dezelfde regel als de
 * Nederlandse Exclusive: de hub nooit (luxeGenericMetadata), een hotelteaser
 * alleen met LUXE_PUBLIC_INDEXING_LIVE aan. Met de schakelaar uit nooit.
 *
 * @param {string} internPad  LUXE_DE_INTERN_PAD of LUXE_DE_INTERN_PAD/<id>
 */
export function luxeDeIndexeerbaar(internPad) {
  if (!luxeDePubliekLive() || !luxePublicIndexingLive()) return false;
  return typeof internPad === "string" && internPad.startsWith(`${LUXE_DE_INTERN_PAD}/`);
}

/** De X-Robots-Tag van een Duitse pagina: null als zij geïndexeerd mag worden, anders noindex. */
export function luxeDeRobots(internPad) {
  return luxeDeIndexeerbaar(internPad) ? null : "noindex, nofollow";
}

/** Het publieke adres van een Duits hotel: /exclusive/<id>, of de hub zonder geldig dealnummer. */
export function luxeDeHotelPad(dealId) {
  const id = luxeDeId(dealId);
  return id ? [EXCLUSIVE_PAD, id].join("/") : EXCLUSIVE_PAD;
}

const EURO_DE = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const EURO_DE_HEEL = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

/**
 * Duitse bedragnotatie: «218 €» (hele euro's zonder decimalen), «218,50 €»,
 * nooit «€ 218,-». Alleen voor een eindig getal; anders null. Voor de Duitse
 * oppervlakken na het inloggen (brief A3); een uitgelogde bezoeker ziet op
 * deze pagina's nooit een bedrag.
 */
export function formatDeEuro(bedrag) {
  if (typeof bedrag !== "number" || !Number.isFinite(bedrag)) return null;
  const centen = Math.round(bedrag * 100);
  return (centen % 100 === 0 ? EURO_DE_HEEL : EURO_DE).format(centen / 100);
}

const DATUM_DE = new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Berlin" });

/** Duitse datum: «10. November 2026». Alleen een echte JJJJ-MM-DD of Date; anders null. */
export function formatDeDatum(waarde) {
  let datum = null;
  if (waarde instanceof Date) datum = waarde;
  else if (typeof waarde === "string" && /^\d{4}-\d{2}-\d{2}$/.test(waarde)) datum = new Date(`${waarde}T12:00:00Z`);
  if (!datum || Number.isNaN(datum.getTime())) return null;
  if (typeof waarde === "string" && datum.toISOString().slice(0, 10) !== waarde) return null;
  return DATUM_DE.format(datum);
}
